import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd, articleJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { KOMMUN_LINKS, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";
import { relatedByCluster } from "@/lib/clusters";

const faqs = [
  { q: "Vart ansöker jag om serveringstillstånd i Malmö?", a: "Ansökan görs till Arbetsmarknads- och socialförvaltningens tillståndsenhet i Malmö stad. Lund, Burlöv, Vellinge och Staffanstorp har egna kommunala enheter." },
  { q: "Vad kostar det i Malmö?", a: "Malmö stad tar ut ansöknings- och tillsynsavgifter som styrs av tillståndstyp och omsättning. Aktuella avgifter finns på kommunens webbplats." },
  { q: "Hur lång är handläggningstiden?", a: "Räkna med 8–12 veckor för ett stadigvarande tillstånd efter komplett ansökan." },
  { q: "Vilka dokument behöver jag?", a: "Registreringsbevis, finansieringsplan, hyresavtal, ritning, brandskydd och uppgifter om PBI-kretsen." },
];

const URL = "https://alkoholprovet.se/serveringstillstand-malmo";

export const Route = createFileRoute("/serveringstillstand-malmo")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Malmö" },
      ]}
      title="Serveringstillstånd Malmö – ansökan och krav"
      intro="Allt om serveringstillstånd i Malmö: var ansökan lämnas, vilka krav som ställs och hur du förbereder dig för kunskapsprovet."
      lastReviewed={LAST_REVIEWED}
      sources={[KOMMUN_LINKS.malmo, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN]}
      localData={{
        unit: "Malmö stad – Arbetsmarknads- och socialförvaltningens tillståndsenhet",
        address: "Storgatan 41A, 211 34 Malmö",
        phone: "040-34 10 00",
        waitTime: "8–12 veckor från komplett ansökan (stadigvarande)",
        feeExamples: [
          "Stadigvarande tillstånd allmänheten: ca 12 000–14 000 kr",
          "Tillfälligt tillstånd allmänheten: ca 6 000–9 000 kr per tillfälle",
          "Tillsynsavgift: fast + rörlig baserat på alkoholomsättning",
        ],
        checklist: [
          "Registreringsbevis",
          "Finansieringsplan och kontoutdrag",
          "Hyresavtal",
          "Ritning över serveringsyta",
          "Brandskyddsdokumentation",
          "PBI-uppgifter och godkänt kunskapsprov",
        ],
        officialUrl: KOMMUN_LINKS.malmo,
      }}
      sections={[
        { h2: "Steg-för-steg: ansökan i Malmö", body: "1. Förbered dokumenten i checklistan.\n2. Lämna in via Malmö stads e-tjänst eller blankett.\n3. Betala ansökningsavgift.\n4. Boka kunskapsprov.\n5. Remisser till polis, räddningstjänst, miljö och skatteverk.\n6. Beslut inom cirka 8–12 veckor från komplett ansökan." },
        { h2: "Krav från Malmö stad", body: "Nationella krav gäller: personlig och ekonomisk lämplighet, godkänt kunskapsprov och lämplig lokal. Från 1 juni 2026 är de nationella kraven på eget kök och varierat matutbud borttagna. Matservering är inte längre ett villkor för tillståndet. Malmö har lokala riktlinjer för uteserveringar längs centrala stråk och hamnområden." },
        { h2: "Kunskapsprovet i Malmö", body: "Provet är skriftligt och genomförs hos kommunen. Frågeantalet varierar beroende på tillståndstyp – Malmö stad uppger aktuella villkor vid bokning. Med Alkoholprovet kan du öva fritt och göra provsimulering i samma format inför skarpt prov." },
        { h2: "Tillsyn i Malmö", body: "Tillsynen är aktiv, särskilt under sommar och helger. Goda rutiner kring ålderskontroll, nykterhet och bokföring är avgörande för att behålla tillståndet." },
      ]}
      faqs={faqs}
      related={relatedByCluster("/serveringstillstand-malmo")}
    />
  ),
  head: () => ({
    meta: [
      { title: "Serveringstillstånd Malmö – krav, prov & tips" },
      { name: "description", content: "Så får du serveringstillstånd i Malmö: lokala krav, tillsynsavgifter och hur du klarar kunskapsprovet." },
      { property: "og:title", content: "Serveringstillstånd Malmö – tips" },
      { property: "og:description", content: "Krav, prov och tips för serveringstillstånd i Malmö." },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Malmö", url: URL },
      ]),
      articleJsonLd({
        url: URL,
        headline: "Serveringstillstånd Malmö – ansökan och krav",
        description: "Komplett guide till serveringstillstånd i Malmö: var du ansöker, krav, avgifter, handläggningstid och kunskapsprov.",
        datePublished: "2026-01-15",
        dateModified: LAST_REVIEWED,
      }),
    ],
  }),
});
